import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, Search, UserRound, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { Paginated, SectionListItem } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/ui";
import { TimetableElementsTable } from "../components/TimetableElementsTable";
import { formatMinutes } from "../lib/domain";
import { downloadSchedulePdf } from "../lib/pdfExport";
import { getCourseSectionColorStyle } from "../lib/scheduleColors";

const days = [
  ["M", "Monday"],
  ["T", "Tuesday"],
  ["W", "Wednesday"],
  ["R", "Thursday"],
  ["F", "Friday"],
] as const;
const startHour = 8;
const endHour = 22;
const hourHeight = 80;
const timetableStart = startHour * 60;
const timetableEnd = endHour * 60;
const timetableHeight = (endHour - startHour) * hourHeight;
const primaryScheduleTypeCodes = ["LEC", "LAB", "TUT", "THS", "SEM", "WRK"] as const;

const scheduleTypes: Record<
  string,
  { label: string; block: string; badge: string }
> = {
  LEC: {
    label: "Lecture",
    block: "border-blue-300 bg-blue-100 text-blue-950",
    badge: "bg-blue-100 text-blue-800",
  },
  LAB: {
    label: "Lab",
    block: "border-emerald-300 bg-emerald-100 text-emerald-950",
    badge: "bg-emerald-100 text-emerald-800",
  },
  TUT: {
    label: "Tutorial",
    block: "border-violet-300 bg-violet-100 text-violet-950",
    badge: "bg-violet-100 text-violet-800",
  },
  THS: {
    label: "Thesis",
    block: "border-amber-300 bg-amber-100 text-amber-950",
    badge: "bg-amber-100 text-amber-800",
  },
  SEM: {
    label: "Seminar",
    block: "border-cyan-300 bg-cyan-100 text-cyan-950",
    badge: "bg-cyan-100 text-cyan-800",
  },
  WRK: {
    label: "Work term",
    block: "border-slate-300 bg-slate-100 text-slate-950",
    badge: "bg-slate-200 text-slate-800",
  },
};

function scheduleTypeDetails(scheduleType: string) {
  const code = scheduleType.trim().toUpperCase() || "OTHER";
  return {
    code,
    ...(scheduleTypes[code] ?? {
      label: code === "OTHER" ? "Other" : code,
      block: "border-indigo-300 bg-indigo-100 text-indigo-950",
      badge: "bg-indigo-100 text-indigo-800",
    }),
  };
}

function scheduleTypeFilterKey(scheduleType: string): string {
  const code = scheduleTypeDetails(scheduleType).code;
  return scheduleTypes[code] ? code : "OTHER";
}

function matchesRoomFilter(meetingRoom: string, roomFilter: string): boolean {
  const normalizedRoomFilter = roomFilter.trim().toLowerCase();
  return !normalizedRoomFilter || meetingRoom.toLowerCase().includes(normalizedRoomFilter);
}

interface ConflictRecord {
  id: string;
  campus: string;
  room: string;
  dayCode: string;
  overlapStart: number;
  overlapEnd: number;
  meetingAId: string;
  meetingBId: string;
  sectionA: { id: string; crn: string; course: string };
  sectionB: { id: string; crn: string; course: string };
}

interface TimetableEntry {
  section: SectionListItem;
  meeting: SectionListItem["meetings"][number];
}

interface PositionedEntry extends TimetableEntry {
  lane: number;
  laneCount: number;
}

function positionDayEntries(entries: TimetableEntry[]): PositionedEntry[] {
  const sorted = [...entries].sort(
    (left, right) =>
      left.meeting.startMinutes - right.meeting.startMinutes ||
      left.meeting.endMinutes - right.meeting.endMinutes,
  );
  const positioned: PositionedEntry[] = [];
  let cluster: Array<TimetableEntry & { lane: number }> = [];
  let clusterEnd = -1;
  let laneEnds: number[] = [];

  function finishCluster() {
    const laneCount = Math.max(laneEnds.length, 1);
    positioned.push(
      ...cluster.map((entry) => ({
        ...entry,
        laneCount,
      })),
    );
    cluster = [];
    clusterEnd = -1;
    laneEnds = [];
  }

  for (const entry of sorted) {
    if (cluster.length > 0 && entry.meeting.startMinutes >= clusterEnd) {
      finishCluster();
    }
    let lane = laneEnds.findIndex(
      (endMinutes) => endMinutes <= entry.meeting.startMinutes,
    );
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(entry.meeting.endMinutes);
    } else {
      laneEnds[lane] = entry.meeting.endMinutes;
    }
    cluster.push({ ...entry, lane });
    clusterEnd = Math.max(clusterEnd, entry.meeting.endMinutes);
  }
  if (cluster.length > 0) finishCluster();
  return positioned;
}

export function TimetablePage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [instructor, setInstructor] = useState(
    () => searchParams.get("instructor") ?? "",
  );
  const [room, setRoom] = useState(() => searchParams.get("room") ?? "");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [visibleScheduleTypes, setVisibleScheduleTypes] = useState<Set<string>>(
    () => new Set([...primaryScheduleTypeCodes, "OTHER"]),
  );
  const [visibleDays, setVisibleDays] = useState<Set<string>>(
    () => new Set(days.map(([day]) => day)),
  );
  const conflictId = searchParams.get("conflict") ?? "";
  const sectionId = searchParams.get("section") ?? "";

  const conflictsQuery = useQuery({
    queryKey: ["conflicts", snapshotTermId, departmentId],
    queryFn: () =>
      apiFetch<ConflictRecord[]>(
        `/conflicts${queryString({
          snapshotTermId,
          department: departmentId,
        })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });
  const selectedConflict = conflictsQuery.data?.find(
    (conflict) => conflict.id === conflictId,
  );
  const conflictSectionIds = selectedConflict
    ? `${selectedConflict.sectionA.id},${selectedConflict.sectionB.id}`
    : "";
  const query = useQuery({
    queryKey: [
      "timetable",
      snapshotTermId,
      departmentId,
      search,
      instructor,
      room,
      sectionId,
      conflictSectionIds,
    ],
    queryFn: () =>
      apiFetch<Paginated<SectionListItem>>(
        `/sections${queryString({
          snapshotTermId,
          department: departmentId,
          pageSize: 100,
          search,
          instructor,
          room,
          sectionId,
          sectionIds: conflictSectionIds,
        })}`,
      ),
    enabled: Boolean(snapshotTermId),
    placeholderData: (previous) => previous,
  });
  const linkedSectionsQuery = useQuery({
    queryKey: ["linked-sections", selectedSectionId],
    queryFn: () =>
      apiFetch<SectionListItem[]>(`/sections/${selectedSectionId}/linked`),
    enabled: Boolean(selectedSectionId),
  });

  const highlightedMeetingIds = useMemo(
    () =>
      new Set(
        selectedConflict
          ? [selectedConflict.meetingAId, selectedConflict.meetingBId]
          : [],
      ),
    [selectedConflict],
  );
  const meetings = (query.data?.items ?? []).flatMap((section) =>
    section.meetings.map((meeting) => ({ section, meeting })),
  );
  const visibleDayList = days.filter(([day]) => visibleDays.has(day));
  const visibleMeetings = meetings.filter(
    ({ section, meeting }) =>
      visibleDays.has(meeting.dayCode) &&
      matchesRoomFilter(meeting.room, room) &&
      visibleScheduleTypes.has(scheduleTypeFilterKey(section.scheduleType)),
  );
  const meetingsByDay = useMemo(
    () =>
      new Map(
        visibleDayList.map(([day]) => [
          day,
          positionDayEntries(
            visibleMeetings.filter(
              ({ meeting }) =>
                meeting.dayCode === day &&
                meeting.startMinutes < timetableEnd &&
                meeting.endMinutes > timetableStart,
            ),
          ),
        ]),
      ),
    [visibleDayList, visibleMeetings],
  );
  const hasFilters = Boolean(search || instructor || room || conflictId || sectionId);

  function toggleScheduleType(code: string) {
    setVisibleScheduleTypes((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function toggleDay(day: string) {
    setVisibleDays((current) => {
      const next = new Set(current);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  function clearFilters() {
    setSearch("");
    setInstructor("");
    setRoom("");
    setSearchParams({});
  }

  function exportPdf() {
    downloadSchedulePdf({
      title: "FBIT Timetable Export",
      subtitle: "Current timetable filters and visible days",
      fileName: "fbit-timetable.pdf",
      days: visibleDayList.map(([code, label]) => ({ code, label })),
      startHour,
      endHour,
      entries: [...meetingsByDay.values()].flatMap((dayEntries) =>
        dayEntries.map(({ section, meeting, lane, laneCount }) => ({
          dayCode: meeting.dayCode,
          subject: section.subject,
          courseNumber: section.courseNumber,
          sectionCode: section.sectionCode,
          sectionTitle: section.sectionTitle,
          scheduleType: section.scheduleType,
          room: meeting.room,
          facultyName: section.facultyName,
          startMinutes: meeting.startMinutes,
          endMinutes: meeting.endMinutes,
          lane,
          laneCount,
          highlighted: highlightedMeetingIds.has(meeting.id),
        })),
      ),
    });
  }

  if (query.isLoading && !query.data) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;

  return (
    <>
      <PageHeader
        title="Digital Twin Timetable"
        description="Search courses, instructors, and rooms. Matching meetings are highlighted in the weekly grid."
      />
      <div className="panel p-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_1fr_220px_auto]">
          <label className="relative">
            <span className="sr-only">Search courses</span>
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              className="field w-full pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Course, title, or CRN"
            />
          </label>
          <label className="relative">
            <span className="sr-only">Filter by instructor</span>
            <UserRound className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              className="field w-full pl-9"
              value={instructor}
              onChange={(event) => setInstructor(event.target.value)}
              placeholder="Instructor name"
            />
          </label>
          <input
            className="field w-full"
            value={room}
            onChange={(event) => setRoom(event.target.value)}
            placeholder="Room"
          />
          <button
            className="button-secondary"
            type="button"
            disabled={!hasFilters}
            onClick={clearFilters}
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-bold uppercase tracking-wider text-slate-400">Schedule type</span>
          {[...primaryScheduleTypeCodes, "OTHER"].map((code) => {
            const type = scheduleTypeDetails(code);
            const enabled = visibleScheduleTypes.has(code);
            return (
              <button
                key={code}
                className={`badge ${type.badge} transition ${enabled ? "" : "opacity-35 ring-1 ring-slate-300"}`}
                type="button"
                onClick={() => toggleScheduleType(code)}
                aria-pressed={enabled}
              >
                {code} - {type.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-bold uppercase tracking-wider text-slate-400">Days</span>
            {days.map(([day, label]) => {
              const enabled = visibleDays.has(day);
              return (
                <button
                  key={day}
                  className={`rounded-full border px-3 py-1 font-bold transition ${
                    enabled
                      ? "border-brand-300 bg-brand-50 text-brand-700"
                      : "border-slate-200 bg-white text-slate-400"
                  }`}
                  type="button"
                  onClick={() => toggleDay(day)}
                  aria-pressed={enabled}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button className="button-secondary" type="button" onClick={exportPdf}>
            <Download className="h-4 w-4" />
            Export PDF
          </button>
        </div>
      </div>
      {selectedConflict && (
        <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
          <p>
            Highlighting conflict in <strong>{selectedConflict.campus} / {selectedConflict.room}</strong>:
            {" "}{selectedConflict.sectionA.course} and {selectedConflict.sectionB.course},{" "}
            {formatMinutes(selectedConflict.overlapStart)}-{formatMinutes(selectedConflict.overlapEnd)}.
          </p>
        </div>
      )}
      {visibleMeetings.length === 0 ? (
        <EmptyState
          title="No visible meetings"
          description="Adjust the course, instructor, room, department, schedule type, or day filters."
        />
      ) : (
        <>
          <div className="panel overflow-x-auto p-4">
            <div
              className="grid"
              style={{
                minWidth: 80 + visibleDayList.length * 170,
                gridTemplateColumns: `80px repeat(${visibleDayList.length}, minmax(170px, 1fr))`,
              }}
            >
              <div className="border-b border-r border-slate-200 bg-slate-50 p-3 text-xs font-bold uppercase text-slate-400">Time</div>
              {visibleDayList.map(([, label]) => <div key={label} className="border-b border-r border-slate-200 bg-slate-50 p-3 text-center text-xs font-bold uppercase text-slate-600">{label}</div>)}
              <div className="border-r border-slate-200">
                {Array.from({ length: endHour - startHour }, (_, index) => startHour + index).map((hour) => (
                  <div
                    className="h-20 border-b border-slate-200 p-2 text-center text-xs font-semibold text-slate-400"
                    key={hour}
                  >
                    {String(hour).padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              {visibleDayList.map(([day]) => (
                <div
                  key={day}
                  className="relative border-r border-slate-200"
                  style={{
                    height: timetableHeight,
                    backgroundImage:
                      "repeating-linear-gradient(to bottom, transparent 0, transparent 79px, rgb(226 232 240) 79px, rgb(226 232 240) 80px)",
                  }}
                >
                  {(meetingsByDay.get(day) ?? []).map(
                    ({ section, meeting, lane, laneCount }) => {
                      const isConflict = highlightedMeetingIds.has(meeting.id);
                      const isMatch = Boolean(search || instructor || room || sectionId);
                      const scheduleType = scheduleTypeDetails(section.scheduleType);
                      const sectionColorStyle = getCourseSectionColorStyle(section);
                      const visibleStart = Math.max(meeting.startMinutes, timetableStart);
                      const visibleEnd = Math.min(meeting.endMinutes, timetableEnd);
                      const top = ((visibleStart - timetableStart) / 60) * hourHeight;
                      const height = Math.max(
                        ((visibleEnd - visibleStart) / 60) * hourHeight,
                        24,
                      );
                      const laneWidth = 100 / laneCount;
                      return (
                        <button
                          key={meeting.id}
                          type="button"
                          onClick={() => setSelectedSectionId(section.id)}
                          className={`absolute overflow-hidden rounded-md border px-2 py-1 text-left text-[10px] shadow-sm transition hover:z-20 hover:brightness-95 focus:z-20 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                            isConflict
                              ? "z-10 border-rose-500 bg-rose-100 text-rose-950 ring-2 ring-rose-400"
                              : isMatch ? "ring-2 ring-amber-400" : ""
                          }`}
                          style={{
                            ...(isConflict ? {} : sectionColorStyle),
                            top,
                            height,
                            left: `calc(${lane * laneWidth}% + 3px)`,
                            width: `calc(${laneWidth}% - 6px)`,
                          }}
                          title={`${section.sectionTitle} - ${section.facultyName}`}
                        >
                          <p className="truncate font-bold">{section.subject} {section.courseNumber}-{section.sectionCode}</p>
                          <p className="truncate font-bold">Sec {section.sectionCode} - {scheduleType.code}</p>
                          <p>{formatMinutes(meeting.startMinutes)}-{formatMinutes(meeting.endMinutes)}</p>
                          <p className="truncate">{meeting.room}</p>
                          <p className="truncate opacity-75">{section.facultyName}</p>
                        </button>
                      );
                    },
                  )}
                </div>
              ))}
            </div>
            {(query.data?.total ?? 0) > 100 && (
              <p className="mt-4 text-xs text-amber-700">The timetable displays the first 100 matching sections. Narrow the filters to reduce the working set.</p>
            )}
          </div>
          <TimetableElementsTable
            title="Timetable Elements"
            description="Table view of the currently visible timetable meetings."
            rows={visibleMeetings.map(({ section, meeting }) => ({
              section,
              meeting,
              highlighted: highlightedMeetingIds.has(meeting.id),
            }))}
            onSectionClick={setSelectedSectionId}
          />
        </>
      )}
      {selectedSectionId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Linked course sections"
        >
          <div className="panel max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">Linked Course Sections</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Lectures, labs, tutorials, theses, and other sections sharing the same term and course code.
                </p>
              </div>
              <button className="button-secondary" type="button" onClick={() => setSelectedSectionId("")}>
                Close
              </button>
            </div>
            {linkedSectionsQuery.isLoading ? (
              <div className="py-10"><LoadingState label="Loading linked sections..." /></div>
            ) : linkedSectionsQuery.error ? (
              <div className="py-6"><ErrorState error={linkedSectionsQuery.error} /></div>
            ) : (
              <div className="mt-5 space-y-3">
                {(linkedSectionsQuery.data ?? []).map((section) => {
                  const type = scheduleTypeDetails(section.scheduleType);
                  return (
                    <article
                      key={section.id}
                      className={`rounded-xl border p-4 ${section.id === selectedSectionId ? "ring-2 ring-brand-500" : ""}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`badge ${type.badge}`}>{type.code} - {type.label}</span>
                            <span className="text-xs font-semibold text-slate-400">CRN {section.crn}</span>
                          </div>
                          <h3 className="mt-2 font-black text-slate-900">
                            {section.subject} {section.courseNumber}-{section.sectionCode}
                          </h3>
                          <p className="text-sm text-slate-500">{section.sectionTitle}</p>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <p className="font-semibold text-slate-700">{section.facultyName}</p>
                          <p>{section.actualEnrollment} / {section.maxEnrollment} enrolled</p>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {section.meetings.map((meeting) => (
                          <span key={meeting.id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                            {meeting.dayCode} {formatMinutes(meeting.startMinutes)}-{formatMinutes(meeting.endMinutes)} - {meeting.campus} / {meeting.room}
                          </span>
                        ))}
                        {section.meetings.length === 0 && (
                          <span className="text-xs text-slate-400">No scheduled physical meeting.</span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
